import { create } from 'zustand';

/**
 * "This item's local draft state is void — throw it away and reseed from the
 * server" — a per-item generation counter, bumped exactly once per completed
 * "Reset to base copy".
 *
 * ── The bug this exists to close (D12 Codex round-2 finding #2) ───────────
 * the V1 dock's `handleResetToBase` (now `EnrollQueueContext.
 * resetItemToBase`) discards the item's context-level autosave entry and
 * PATCHes the row back to base-following. That is the whole story ONLY while
 * the item's editor is closed. With `EnrollmentItemEditor` mounted for that
 * same item, the editor keeps its OWN local state that the reset never
 * touched:
 *
 *   - `useDraftState` applies a seed exactly ONCE per entity identity and
 *     deliberately ignores later seed changes (CLAUDE.md "Draft State Under
 *     SSE"), and `item.id` does not change on a reset — so the editor went on
 *     DISPLAYING the just-discarded subject/body.
 *   - The copy live-follow effect is gated on `copyEdited`, whose ref was
 *     still set to this item, so even the reseed path that does exist was
 *     blocked.
 *   - The sender live-follow effect only fires for a non-null `senderPrefill`,
 *     so with nothing staged for the play there was nothing to reseed TO.
 *
 * Net effect: after a Reset the open editor still showed the old values, and
 * the rep's very next keystroke (or Send) re-persisted the exact values Reset
 * had just thrown away.
 *
 * ── Why a generation counter rather than a callback ──────────────────────
 * The editor's four local-write refs (`senderWrite`/`copyWrite`/
 * `senderPicked`/`copyEdited`) already store an ITEM ID rather than a boolean,
 * precisely so switching rows invalidates them BY CONSTRUCTION — no
 * mount-lifetime flag for StrictMode's setup→cleanup→setup replay to leave
 * stuck, and no reset effect to keep in sync. Folding this counter into that
 * same key (`${item.id}:${generation}`) extends that property to resets for
 * free: one bump invalidates all four refs AND re-keys `useDraftState`, whose
 * documented render-phase guard then reseeds the draft from the current seed.
 * A reset callback threaded down the prop chain
 * (`RailDock` → `PlaysPane` → the play rows →
 * `AssignedRow` → `EnrollmentItemEditor`) would need a registration protocol,
 * a per-instance identity, and its own StrictMode story — all to reproduce a
 * guard the editor already has.
 *
 * Module-scope (the `zebraStripes`/`panel-width-store` precedent, CLAUDE.md
 * "Zustand for Module-Scope Client State") rather than per-provider: the key
 * space is `enrollment_items.id`, globally unique, and exactly one
 * `EnrollQueueProvider` is mounted app-wide. Deliberately NOT persisted — a
 * generation is a within-session signal, not state worth surviving a reload
 * (after a reload the editor is unmounted and reseeds from the server
 * anyway), so it stays out of `staged-play-config`'s localStorage blob.
 *
 * Entries are never pruned. A counter per reset-in-this-session is bounded by
 * how many rows a rep resets before a reload (single digits), each entry is
 * one number, and pruning would need the queue-departure sweep to reach into
 * this module for no benefit — a departed item's stale counter is inert.
 */

/**
 * "What the item must look like for the reset to count as DELIVERED to React"
 * — the post-reset trio `resetItemToBase` PATCHed, in the same normalized
 * shape `EnrollQueueContext.itemReflectsPatch` compares against (blank ⇒
 * NULL server-side, so a nulled draft reads back as `''` here).
 *
 * D12 Codex round-3 finding #6 — see `requestItemDraftReset` below.
 */
export interface ItemDraftResetExpectation {
  sender: string;
  subject_draft: string;
  body_draft: string;
}

export interface ItemDraftResetState {
  /** `enrollment_items.id` -> completed-reset count. Absent means zero. */
  generations: Record<number, number>;
  /**
   * `enrollment_items.id` -> the post-reset trio a landed reset is still
   * WAITING for React to actually render (D12 Codex round-3 finding #6).
   * Absent means "no reset in flight for this item".
   */
  pending: Record<number, ItemDraftResetExpectation>;
}

/** Exported so tests reset with `setState(initialState, true)` (repo convention). */
export const initialState: ItemDraftResetState = { generations: {}, pending: {} };

export const useItemDraftResetStore = create<ItemDraftResetState>(() => ({
  generations: {},
  pending: {},
}));

/**
 * ── D12 Codex round-3 finding #6 — why the bump is now TWO steps ──────────
 *
 * Round 2 bumped the generation synchronously on the line after
 * `await queryClient.invalidateQueries({ queryKey: QUEUE_KEY })` resolved,
 * on the reasoning that the queue read had landed by then. That is true of
 * the query CACHE and false of REACT: nothing orders React Query's observer
 * notification (a batched microtask through `notifyManager`) against the
 * awaiting continuation, so the bump can run in a tick where the editor's
 * parent has NOT yet re-rendered with the post-reset item.
 *
 * When it does, the bump changes `draftKey` FIRST and `useDraftState`'s
 * documented render-phase guard reseeds from the `item` prop the editor still
 * holds — the PRE-reset one — permanently seeding the new generation from
 * stale data. The copy live-follow effect later self-corrects copy; there is
 * no equivalent mechanism for SENDER (its live-follow effect only fires for a
 * non-null `senderPrefill`, which a play with nothing staged never has), so a
 * stale sender gets baked into the draft and the editor's own Send PATCHes it
 * straight back to the server.
 *
 * So a landed reset now RECORDS what it is waiting for, and the provider's
 * own effect — which by definition runs after React committed a render
 * carrying the new `groups` (and therefore the editor's new `item` prop) —
 * settles it. The bump can no longer outrun the data it exists to reseed
 * from, and no new ordering assumption is introduced: "React has the data"
 * is expressed as "we are inside an effect for that data".
 */
export function requestItemDraftReset(itemId: number, expected: ItemDraftResetExpectation): void {
  useItemDraftResetStore.setState((state) => ({
    ...state,
    pending: { ...state.pending, [itemId]: expected },
  }));
}

/**
 * The second half of `requestItemDraftReset`: the post-reset item data has
 * reached React, so bump the generation and drop the pending record in ONE
 * store write — two writes would briefly publish a state where the editor
 * re-keys while the provider still believes a reset is outstanding.
 */
export function settleItemDraftReset(itemId: number): void {
  useItemDraftResetStore.setState((state) => {
    if (!(itemId in state.pending)) return state;
    const pending = { ...state.pending };
    delete pending[itemId];
    return {
      generations: { ...state.generations, [itemId]: (state.generations[itemId] ?? 0) + 1 },
      pending,
    };
  });
}

/**
 * Drops a pending reset WITHOUT bumping — the item left the queue (sent,
 * removed) before its post-reset data could be observed, so there is no
 * mounted editor left to reseed.
 */
export function dropItemDraftReset(itemId: number): void {
  useItemDraftResetStore.setState((state) => {
    if (!(itemId in state.pending)) return state;
    const pending = { ...state.pending };
    delete pending[itemId];
    return { ...state, pending };
  });
}

/**
 * Reactive read of the pending map — the provider's settle effect depends on
 * it, so REGISTERING a reset re-runs that effect even when `groups` did not
 * change in the same commit (the ordering that made a plain provider ref
 * insufficient here).
 */
export function usePendingItemDraftResets(): Record<number, ItemDraftResetExpectation> {
  return useItemDraftResetStore((s) => s.pending);
}

/** Reactive read of one item's generation — `EnrollmentItemEditor`'s draft key. */
export function useItemDraftReset(itemId: number): number {
  return useItemDraftResetStore((s) => s.generations[itemId] ?? 0);
}
